<template>
  <!-- Need to add height inherit because Vue 2 don't support multiple root ele -->
  <div
    style="height: inherit"
    :style="showLoader ? 'pointer-events: none; opacity: .5;' : ''"
  >
    <!-- Loader -->
    <b-progress
      v-if="showLoader"
      animated
      :value="100"
      height="5px"
      variant="primary"
      class="progress-bar-primary"
    />

    <!-- Overlay from sidebar -->
    <div
      class="body-content-overlay"
      :class="{'show': mqShallShowLeftSidebar}"
      @click="mqShallShowLeftSidebar = false"
    />

    <!-- Notification List -->
    <div class="email-app-list">
      <!-- Toggler y Filtros aplicados -->
      <div class="app-fixed-search d-flex align-items-center">
        <!-- Toggler menu -->
        <div class="sidebar-toggle d-block d-lg-none ml-1 my-50">
          <feather-icon
            icon="MenuIcon"
            size="21"
            class="cursor-pointer"
            @click="mqShallShowLeftSidebar = true"
          />
        </div>
        <!-- Filtros aplicados -->
        <div class="my-50">
          <!-- Tipo de notificación -->
          <div class="d-inline">
            <small class="text-muted ml-75 mr-25"><b>Viendo:</b></small>
            <b-badge
              variant="light-secondary"
            >
              {{ folderName[filters.folder] }}
            </b-badge>
          </div>
          <!-- Prioridad -->
          <div
            v-if="filters.priority"
            class="d-inline"
          >
            <small class="text-muted ml-75 mr-25"><b>Prioridad:</b></small>
            <b-badge variant="light-secondary">
              {{ filters.priority }}
            </b-badge>
          </div>
          <!-- Dirigido a -->
          <div
            v-if="filters.destiny"
            class="d-inline"
          >
            <small class="text-muted ml-75 mr-25"><b>Dirigido a:</b></small>
            <b-badge variant="light-secondary">
              {{ filters.destiny }}
            </b-badge>
          </div>
        </div>
      </div>

      <!-- Notifications List -->
      <vue-perfect-scrollbar
        :settings="perfectScrollbarSettings"
        class="email-user-list scroll-area"
      >
        <!-- Si tengo notificaciones -->
        <ul class="email-media-list">
          <b-media
            v-for="data in notificaciones"
            :key="data.idNotificacion"
            tag="li"
            no-body
            :class="{ 'mail-read': data.visto }"
            @click="showNotificationDetails = true; notificationSelected = data.idNotificacion"
          >
            <!-- Imagen default -->
            <b-media-aside class="media-left mr-50">
              <b-media no-body>
                <b-avatar
                  variant="light-secondary"
                  size="42"
                >
                  <b-img
                    width="22"
                    :src="require('@/assets/images/icons/speaker.svg')"
                  />
                </b-avatar>
              </b-media>
            </b-media-aside>

            <!-- Notification preview -->
            <b-media-body>
              <!-- Header -->
              <div class="mail-details">
                <!-- Título y fecha -->
                <div class="mail-items">
                  <h5 class="mb-25">
                    {{ data.titulo }}
                  </h5>
                  <small class="text-muted">{{ timestampToDate(data.fecha, 'LLLL') }}</small>
                </div>
                <!-- Indicador de liga, prioridad y dirigido a -->
                <div class="text-right">
                  <!-- Indicador de liga -->
                  <feather-icon
                    v-if="data.url"
                    v-b-tooltip.hover.bottom="'Notificación con liga'"
                    icon="LinkIcon"
                  />
                  <!-- Prioridad -->
                  <span
                    v-b-tooltip.hover.bottom="'Prioridad'"
                    class="mx-50 bullet bullet-sm"
                    :class="`bullet-${notificationPriority(data.prioridad)}`"
                  />
                  <!-- Dirigido a -->
                  <b-badge
                    v-b-tooltip.hover.bottom="'Dirigido a'"
                    variant="light-secondary"
                  >
                    {{ data.tabla_destino }}
                  </b-badge>
                </div>
              </div>

              <!-- Mensaje -->
              <div class="mail-message">
                <!-- eslint-disable vue/no-v-html -->
                <div
                  class="text-truncate text-muted mb-0"
                  style="max-height: 2rem;"
                  v-html="data.mensaje"
                />
                <!-- eslint-enable -->
              </div>
            </b-media-body>
          </b-media>
        </ul>

        <!-- No tengo notificaciones -->
        <div
          class="no-results"
          :class="{'show': !notificaciones.length}"
        >
          <div class="mt-75">
            <b-avatar
              variant="light-secondary"
              size="65"
              class="shadow mb-1"
            >
              <b-img
                width="30"
                :src="require('@/assets/images/icons/speaker.svg')"
              />
            </b-avatar>
            <b-card-text class="m-auto w-75">
              <b>No</b> tienes notificaciones.
            </b-card-text>
          </div>
        </div>
      </vue-perfect-scrollbar>
    </div>

    <!-- Notification View/Detail -->
    <notification-view
      :class="{'show': showNotificationDetails}"
      :notification-id="notificationSelected"
      @close-notification-view="showNotificationDetails = false"
    />

    <!-- Sidebar -->
    <portal to="content-renderer-sidebar-left">
      <notification-left-sidebar
        :notificaciones-meta="notificacionesMeta"
        :class="{'show': mqShallShowLeftSidebar}"
        :show-notification-compose-modal.sync="showNotificationComposeModal"
        :show-emitted-notification-modal.sync="showEmittedNotificationModal"
        @close-left-sidebar="mqShallShowLeftSidebar = false"
        @close-notification-view="showNotificationDetails = false"
        @change-folder="filters.folder = $event"
        @change-priority="filters.priority = $event"
        @change-destino="filters.destiny = $event"
        @change-order="filters.isSortDesc = $event"
        @reload-notification="loadNotifications"
      />
    </portal>

    <!-- Compose Notification Modal -->
    <notification-compose v-model="showNotificationComposeModal" />

    <!-- Emitted Notification Modal -->
    <notification-emitted v-model="showEmittedNotificationModal" />
  </div>
</template>

<script>
import {
  ref,
  onMounted,
  watch,
} from '@vue/composition-api'
import {
  BProgress,
  VBTooltip,
  BBadge,
  BMedia,
  BMediaAside,
  BMediaBody,
  BAvatar,
  BCardText,
  BImg,
} from 'bootstrap-vue'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import {
  timestampToDate,
  notificationPriority,
} from '@core/utils/filter'
import { useRouter } from '@core/utils/utils'
import { useResponsiveAppLeftSidebarVisibility } from '@core/comp-functions/ui/app'
import NotificationLeftSidebar from './NotificationLeftSidebar.vue'
import NotificationCompose from './NotificationCompose.vue'
import NotificationEmitted from './NotificationEmitted.vue'
import NotificationView from './NotificationView.vue'
import useNotificacion from './useNotificacion'

export default {
  components: {
    BProgress,
    BBadge,
    BMedia,
    BMediaAside,
    BMediaBody,
    BAvatar,
    BCardText,
    BImg,

    // 3rd Party
    VuePerfectScrollbar,

    // App SFC
    NotificationLeftSidebar,
    NotificationView,
    NotificationCompose,
    NotificationEmitted,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  setup() {
    const { route } = useRouter()
    const { open } = route.value.query
    const perfectScrollbarSettings = { maxScrollbarLength: 150, wheelPropagation: false }
    const {
      showLoader,
      filters,
      notificaciones,
      loadNotifications,
      notificacionesMeta,
    } = useNotificacion()
    const showNotificationDetails = ref(false)
    const showNotificationComposeModal = ref(false)
    const showEmittedNotificationModal = ref(false)
    const notificationSelected = ref(null)

    const openItem = id => {
      showNotificationDetails.value = true
      notificationSelected.value = parseInt(id, 10)
    }

    onMounted(() => {
      loadNotifications()
      if (open && !Number.isNaN(open)) openItem(open)
    })

    watch(() => route.value.query.open, val => {
      if (val && !Number.isNaN(val)) openItem(val)
    })

    // Left Sidebar Responsiveness
    const { mqShallShowLeftSidebar } = useResponsiveAppLeftSidebarVisibility()

    const folderName = {
      unread: 'Nuevas',
      read: 'Anteriores',
      all: 'Todas',
    }

    return {
      // Method
      loadNotifications,

      // Data
      filters,
      notificaciones,
      notificacionesMeta,
      folderName,

      // UI
      perfectScrollbarSettings,

      // Loader
      showLoader,

      // Notification Details
      showNotificationDetails,
      notificationSelected,

      // Modal
      showNotificationComposeModal,
      showEmittedNotificationModal,

      // UI Filters
      timestampToDate,
      notificationPriority,

      // Left Sidebar Responsiveness
      mqShallShowLeftSidebar,
    }
  },
}
</script>

<style lang="scss">
@import "~@core/scss/base/pages/app-email.scss";
</style>
